import { useState, useRef, useCallback, useEffect } from "react";
import bjLogo from "./assets/bj-titan-wordmark.webp";
import thinkingIcon from "./assets/head-gear.webp";
import { acquireBackendToken, getActiveAccount, getAuthError, subscribeAccount, signIn, signOut } from "./auth";
import { fmt } from "./format";

/* ── GOVERNED-BACKEND BRIDGE CONFIG (safe cutover) ──
   The governed-backend chat path stays OFF until the backend is provisioned and a real
   cited query is validated (gate J3), then REACT_APP_BACKEND_MODE is flipped to "on".
   Default OFF preserves the existing direct-to-Anthropic behavior EXACTLY. The connection
   indicator (always on) polls the backend regardless, so readiness is visible before cutover. */
// Defaults to the known prod App Service so the connection indicator is live on deploy even
// before the pipeline var is set (it honestly shows "unreachable" until Dylan adds the SWA
// origin to CORS, then flips). Override via REACT_APP_BACKEND_URL for local dev.
const BACKEND_URL  = (process.env.REACT_APP_BACKEND_URL ||
  "https://app-everest-api-prod-c-byagfrf7ejbxh5by.southcentralus-01.azurewebsites.net"
).replace(/\/$/, "");
const USE_BACKEND  = !!BACKEND_URL;

// Frontend role labels → backend UserRole enum values (everest.enums.UserRole).
const ROLE_TO_BACKEND = {
  "Field Operator": "FieldOperator", "Field Leader": "FieldLeader",
  "Maintenance": "Maintenance", "Engineering": "Engineering", "EHSR": "EHSR",
  "Operations Supervisor": "OpsSupervisor", "Executive": "Executive", "IT": "IT",
  "Project Lead": "ProjectLead",  // backend admin (UserRole.PROJECT_LEAD); must be present or a PL token can't map.
};
// Reverse map: backend UserRole value (carried in the Entra ID token's `roles` claim) →
// frontend label. Lets governed mode derive the signed-in user's role from BJ SSO (no dropdown).
const BACKEND_TO_ROLE = Object.fromEntries(
  Object.entries(ROLE_TO_BACKEND).map(([label, value]) => [value, label])
);

/* Acquire an Entra bearer token for the backend API via MSAL (BJ SSO). Returns the signed-in
   user's ID token, which the backend validates against this app registration (aud == clientId,
   iss == tenant/v2.0) and reads the app-role claim from. A window override supports manual
   testing. Returns null if not signed in — the backend then returns 401, which the UI surfaces
   as a sign-in prompt. See auth.js / authConfig.js. */
async function getBackendAuthToken() {
  if (typeof window !== "undefined" && window.__EVEREST_TOKEN__) return window.__EVEREST_TOKEN__;
  return acquireBackendToken();
}
 
/* ── BRAND TOKENS ── */
const BJ = {
  // Official BJ Energy palette (sampled from the Equipment Investigator UI / BJ brand).
  blue:"#00427B", blueDark:"#003366", blueMid:"#1A5A95", blueLight:"#E6F1FB", blueBorder:"#B5D4F4",
  // Secondary accent kept in the BJ-blue family (replaces the prior orange).
  orange:"#1A5A95", orangeLight:"#E6F1FB",
  red:"#C8102E", redLight:"#FEECEC",
  green:"#27AE60", greenLight:"#E8F5E9",
  amber:"#F39C12", amberLight:"#FFF8E1",
  surface:"#F4F6F8", surface2:"#ECEDF0", card:"#FFFFFF",
  border:"rgba(0,0,0,0.10)", borderMid:"rgba(0,0,0,0.15)",
  textPri:"#1A1A1A", textSec:"#4A5462", textMut:"#6B7280",
};
 
/* ── ROLE CONFIG ── */
const ROLES = [
  { value:"Field Operator",        prompts:["Walk me through the TITAN prime-up procedure","What do I do if the turbine fails to start?","What H2S PPE is required on site?","How do I use the Webasto heater on a Propel unit?"] },
  { value:"Field Leader",          prompts:["What is the escalation path for a high-pressure trip?","What are crew briefing STEACS requirements?","Walk me through rate reduction on multiple pumps","What does the TSI Manifold daily inspection cover?"] },
  { value:"Maintenance",           prompts:["What are the QEM5000 lube system intervals?","How is the TF50F lube system configured?","What does the gearbox lube schematic show?","What are the hydraulic system specs for TITAN?"] },
  { value:"Engineering",           prompts:["Explain the TITAN pneumatic system schematic","What does ETM-09 cover for high-pressure treating?","What are the fluid end maintenance thresholds?","Summarize the plunger lube system design"] },
  { value:"EHSR",                  prompts:["What does OS-15 require for H2S safety?","Walk me through the MOC process OS-05","What are the H2S alarm and evacuation procedures?","What does OS-01 define for risk identification?"] },
  { value:"Operations Supervisor", prompts:["What is the rate reduction procedure for grouped pumps?","What is the no-go zone protocol during rate changes?","What are the key operating standards for frac ops?","What fleet NPT signals should I watch?"] },
  { value:"Executive",             prompts:["What is the Project Everest roadmap?","What are the six platform pillars?","What KPIs measure platform success?","What board-level decisions are pending?"] },
  { value:"IT",                    prompts:["What is the Project Everest architecture?","What security standards apply — ITS-04 and ITS-03?","What is the AI governance policy ITS-11?","What is the document classification structure?"] },
  { value:"Project Lead",          prompts:["What is the current Knowledge Library ingestion coverage?","What governance and confidentiality rules apply to ingested documents?","What are the open risks and escalations across the platform?","Summarize the Project Everest roadmap and pillar status."] },
];
 
/* ── DEPTH CONFIG ── */
const DEPTHS = [
  { key:"basic",    label:"Basic",    dot:BJ.green,  desc:"Plain-language answers. Best for field operators and quick lookups." },
  { key:"medium",   label:"Medium",   dot:BJ.amber,  desc:"Adds context, parameters, and step-level detail. For supervisors and maintenance." },
  { key:"advanced", label:"Advanced", dot:BJ.red,    desc:"Engineering depth — specs, schematics, RCA logic. For engineering roles." },
];
 
const DEPTH_INST = {
  basic:    "Respond in plain, direct language a field operator can act on immediately. Avoid jargon. Use short sentences and bullet points.",
  medium:   "Include operational context — relevant parameters, step-level detail, and when to escalate. Suitable for supervisors and maintenance technicians.",
  advanced: "Respond at full engineering depth — reference specific document sections, parameters, schematic relationships, and RCA considerations.",
};
 
/* ── SYSTEM PROMPT ── */
const SYSTEM = (role, depth) => `You are the Project Everest AI — BJ Energy Solutions' governed equipment intelligence assistant.
 
USER ROLE: ${role}
DEPTH: ${depth.toUpperCase()} — ${DEPTH_INST[depth]}
 
RULES:
- Answer directly and concisely. Do not hedge excessively.
- Every BJ-specific factual claim must reference a source document by name (e.g. OS-15, ETM-09, WI-TITAN-Prime-Up).
- If you do not have an approved source, say so clearly and route the user to the right escalation path: equipment/technical → Field Leader or Maintenance → the equipment SME (TITAN SME for TITAN, Truduo SME for the Truduo blender) → Equipment Engineering; operations → Field Leader → Operations Supervisor → Frac Manager. BJ no longer has Field Service Engineers or OSE — never use those terms.
- For anything involving pressure, turbine, electrical, interlocks, H2S, or treating iron — start with ⚠️ SAFETY NOTICE and require SME sign-off before action.
- End responses with a short 📎 Sources: line listing referenced documents.
 
BJ ENERGY CONTEXT:
- Equipment: TITAN turbine direct-drive frac pumps, Truduo systems, PowerGen, TSI Manifold trailers, instrumentation skids.
- 5 TITAN fleets across Canada and US.
- Key documents: OS-01 to OS-21 (Operating Standards), ETM-09 (High-Pressure Treating Equipment), ETM-EES-XX (TITAN Engineering Technical Manual Rev 2), WI prefix (Work Instructions), ITS-01 to ITS-11 (IT/Security Standards).
- Key systems: QEM5000 lube, TF50F lube, gearbox lube, plunger lube, hydraulic, pneumatic, diesel fuel, air/oil drain, waste drain.
- Schematics: FPFT02S7100 (Hydraulic), FPFT02S7201 (Pneumatic), FPFT02S7300 (Plunger Lube), FPFT02S7301 (QEM5000 Lube), FPFT02S7302 (Gearbox Lube), FPFT02S7303 (TF50F Lube), FPFT02S7304 (Air/Oil Drain), FPFT02S7500 (Diesel Fuel), FPFT02S7900 (Waste Drain).`;
 
/* ── GLOBAL STYLE (injected once into <head>, persistently) ──
   These rules previously lived in the Setup screen's <style>, which UNMOUNTS after launch —
   leaving answer bullets un-indented, code blocks unstyled, the "Thinking…" dots frozen (their
   `bn` keyframes were gone), and the app off the Barlow font. Injected once so they survive
   every view and the Setup screen's removal in governed mode. Formatting is scoped to `.ev-rich`
   (the rendered-answer containers) so it never shifts app layout; only @font-face + keyframes
   are global (no box-model side effects). */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Barlow:ital,wght@0,300;0,400;0,500;0,600;0,700;1,700&family=Barlow+Condensed:ital,wght@0,600;0,700;1,700&display=swap');
@keyframes slideUp{from{transform:scale(.96) translateY(12px);opacity:0}to{transform:scale(1) translateY(0);opacity:1}}
@keyframes bn{0%,80%,100%{transform:scale(.6);opacity:.3}40%{transform:scale(1);opacity:1}}
.ev-rich ul,.ev-rich ol{padding-left:24px;margin:6px 0}
/* ── MOTION SYSTEM (Workstream E) ── reusable tokens + entrance keyframes + button
   micro-interactions. Durations/easings anchor to the existing slideUp feel. The
   prefers-reduced-motion block neutralizes ALL motion (incl. slideUp/bn and smooth
   scroll) for accessibility (WCAG 2.1 AA) — motion is never the only signal. */
:root{
  --ev-dur-fast:120ms;--ev-dur-base:200ms;--ev-dur-enter:280ms;
  --ev-ease-out:cubic-bezier(.22,1,.36,1);--ev-ease-soft:cubic-bezier(.4,0,.2,1);
}
@keyframes evRise{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
@keyframes evPop{from{opacity:0;transform:scale(.96)}to{opacity:1;transform:scale(1)}}
@keyframes evFade{from{opacity:0}to{opacity:1}}
@keyframes evMenu{from{opacity:0;transform:scale(.97) translateY(-4px)}to{opacity:1;transform:scale(1) translateY(0)}}
.ev-btn{transition:background var(--ev-dur-fast) var(--ev-ease-soft),transform var(--ev-dur-fast) var(--ev-ease-soft),box-shadow var(--ev-dur-fast) var(--ev-ease-soft),border-color var(--ev-dur-fast) var(--ev-ease-soft),color var(--ev-dur-fast) var(--ev-ease-soft)}
.ev-btn:not(:disabled):hover{transform:translateY(-1px)}
.ev-btn:not(:disabled):active{transform:scale(.97)}
.ev-btn:focus-visible{outline:2px solid #00427B;outline-offset:2px}
@media (prefers-reduced-motion: reduce){
  *,*::before,*::after{animation-duration:.001ms!important;animation-iteration-count:1!important;transition-duration:.001ms!important;scroll-behavior:auto!important}
}
.ev-rich li{margin:3px 0}
.ev-rich pre{background:#F5F6F8;border:1px solid rgba(0,0,0,.1);border-radius:8px;padding:10px 12px;font-size:12px;overflow-x:auto;margin:8px 0;white-space:pre-wrap;word-break:break-word}
.ev-rich code{background:#F5F6F8;padding:1px 5px;border-radius:4px;font-size:12px}
.ev-rich h2,.ev-rich h3,.ev-rich h4{color:#00427B;margin:8px 0 3px}
.ev-rich p{margin:3px 0}
.ev-rich b{font-weight:600}
::-webkit-scrollbar{width:5px}::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:3px}
`;

/* ── MARKDOWN FORMATTER ──
   fmt() and its table/list helpers live in ./format.js (unit-tested in format.test.js). */

/* ── AUTO-TAGS ── */
function tags(text) {
  const t = [];
  if (/troubleshoot|trip|fault|alarm|fail/i.test(text))          t.push({k:"ts",l:"🔧 Troubleshooting",bg:BJ.blueLight,  c:BJ.blue});
  if (/safety|hazard|H2S|PPE|emergency|EHSR/i.test(text))        t.push({k:"sf",l:"🛡 Safety",          bg:"#FCE4EC",      c:"#880E4F"});
  if (/maintenance|interval|lube|service|inspect/i.test(text))   t.push({k:"mn",l:"🔩 Maintenance",    bg:BJ.amberLight,  c:"#6D4C00"});
  if (/escalat|SME|engineer|contact your/i.test(text))           t.push({k:"es",l:"📞 Escalate",        bg:BJ.redLight,    c:BJ.red});
  if (t.length===0)                                               t.push({k:"kn",l:"📖 Knowledge",      bg:BJ.greenLight,  c:"#1B5E20"});
  return t;
}
 
/* ── SHARED COMPONENTS ── */
function BJLogo({size=36}) {
  // Official BJ | TITAN wordmark (bj-titan-wordmark.webp, navy on transparent) on a
  // white chip so the navy mark stays legible on the navy sidebar / header. The chip
  // has no fixed width, so it grows to the wordmark's ~4.5:1 aspect automatically.
  return (
    <div style={{background:"#fff",borderRadius:6,
      height:size,display:"flex",alignItems:"center",justifyContent:"center",
      padding:"4px 10px",boxSizing:"border-box",flexShrink:0}}>
      <img src={bjLogo} alt="BJ TITAN" style={{height:size*0.6,width:"auto",display:"block"}}/>
    </div>
  );
}
 
function Dot({color,size=8}) {
  return <div style={{width:size,height:size,borderRadius:"50%",background:color,flexShrink:0}}/>;
}

/* ── CONNECTION-STATUS HELPERS (R2) ── */
// health: undefined = checking, null = unreachable, object = /health/status payload.
function deriveStatus(health) {
  if (health === undefined) return {color:BJ.amber, label:"Checking knowledge base…"};
  if (!health)              return {color:BJ.red,   label:"Knowledge base: unreachable"};
  // GEN-002: the backend reports consecutive generation failures at /health/status.
  // Retrieval and embeddings ride a different provider and credential, so during the
  // 2026-08-18 outage every other signal here stayed green while no answer could be
  // produced. Surface it, or the status light repeats that lie.
  const gen = (health.generation || {}).status;
  if (gen === "failing") {
    return {color:BJ.red, label:"Answer generation is failing — system issue"};
  }
  const ready =
    health.database === "ok" && health.embeddings === "azure" && (health.chunks_embedded||0) > 0;
  if (!ready) return {color:BJ.amber, label:"Backend online — knowledge base not ready"};
  return gen === "degraded"
    ? {color:BJ.amber, label:"Knowledge base ready — recent generation errors"}
    : {color:BJ.green, label:"Knowledge base ready"};
}

function StatusRow({label, state, value}) {
  const color = state === "ok" ? BJ.green : state === "info" ? BJ.blueMid : BJ.amber;
  const mark  = state === "ok" ? "✓" : state === "info" ? "•" : "○";
  return (
    <div style={{display:"flex",alignItems:"center",gap:8,marginTop:3}}>
      <span style={{color,fontWeight:700,width:12,textAlign:"center"}}>{mark}</span>
      <span style={{width:108,color:BJ.textSec}}>{label}</span>
      <span style={{color:BJ.textPri}}>{value}</span>
    </div>
  );
}

/* ── GOVERNED BACKEND ANSWER (R3) ── */
// Retained, deliberately unrendered. The confidence chip was removed under PL decision
// 2.8a (its provenance-header replacement was itself removed 2026-10-04) because the
// underlying band is not yet trustworthy. This
// map is the sunset path: it comes back when a Phase C re-baseline demonstrates band
// accuracy, or when PROP-016's evidence bands ship — whichever lands first, and never on
// a bare date. Deleting it would only mean rewriting it from the same spec later.
const BAND_STYLE = {
  high:   {bg:BJ.greenLight, c:"#1B5E20", label:"High confidence — verify with source"},
  medium: {bg:BJ.amberLight, c:"#6D4C00", label:"Medium confidence — verify with source"},
  low:    {bg:BJ.redLight,   c:BJ.red,    label:"Low confidence"},
};
// B6.26 (PL ruling 2026-09-04): a LOW-band answer is now shown rather than suppressed
// (backend flag low_band_answers_shown), so it carries this caveat directly above the
// answer text. This is NOT the confidence chip above — ruling 2.8a still stands; it is a
// LOW-only warning. Single constant so the wording changes in one place. The wording below
// is final, from the FORGE review (2026-09-24).
// Wording from the FORGE review (2026-09-24): names the full escalation path and puts the
// approved WI/ETM ahead of the answer, because B6.26 lets LOW answers reach operators.
const LOW_BAND_CAVEAT = "Low confidence — the cited sources only partly support this answer; do not act on it alone, and follow the approved WI/ETM and its safety controls. Confirm with your Field Leader or Maintenance, escalating to the equipment SME (TITAN SME / Truduo SME) and then Equipment Engineering.";
const fbBtn = {background:BJ.surface,border:`1px solid ${BJ.borderMid}`,borderRadius:14,
  padding:"3px 10px",fontFamily:"inherit",fontSize:12,color:BJ.textSec,cursor:"pointer"};
// Full 7-reaction taxonomy (Learning Hub Increment 1, H_synthesis §2 item 11). "unsafe" gets a
// dedicated red accent (#EE0000, per dev-lead spec — distinct from BJ.red/#C8102E used for the
// confidence band, so a safety reaction reads as a different, higher-severity signal).
const REACTIONS = [
  {v:"correct",     label:"👍 Correct"},
  {v:"incomplete",  label:"👎 Incomplete"},
  {v:"wrong_source",label:"📎 Wrong source"},
  {v:"outdated",    label:"🕒 Outdated"},
  {v:"unsafe",      label:"⚠ Unsafe"},
  {v:"needs_sme",   label:"🎓 Needs SME"},
  {v:"other",       label:"⋯ Other"},
];
const ISSUE_CATEGORIES = [
  {v:"wrong_source", label:"Wrong source"},
  {v:"outdated",      label:"Outdated"},
  {v:"incomplete",    label:"Incomplete"},
  {v:"unsafe",        label:"Unsafe"},
  {v:"logic_gap",     label:"Logic gap"},
  {v:"missing_coverage", label:"Not covered in documents"},
  {v:"other",         label:"Other"},
];
// Default category = the clicked reaction when it maps 1:1 onto an issue_category value,
// else "other" (e.g. needs_sme has no direct category counterpart).
const defaultCategoryFor = reaction => ISSUE_CATEGORIES.some(c=>c.v===reaction) ? reaction : "other";
// Chip style: selected + "unsafe" both get a distinct treatment; unselected chips share fbBtn.
function fbChipStyle(reactionValue, isActive) {
  if (reactionValue === "unsafe") {
    return {...fbBtn, border:"1px solid #EE0000", fontWeight:700,
      color: isActive ? "#fff" : "#EE0000", background: isActive ? "#EE0000" : fbBtn.background};
  }
  if (isActive) return {...fbBtn, background:BJ.blueLight, border:`1px solid ${BJ.blue}`, color:BJ.blue, fontWeight:700};
  return fbBtn;
}
const fbInput = {width:"100%",padding:"6px 8px",borderRadius:8,border:`1px solid ${BJ.borderMid}`,
  fontFamily:"inherit",fontSize:13,color:BJ.textPri,background:"#fff",boxSizing:"border-box"};
const fbLabel = {display:"block",fontSize:11,fontWeight:600,color:BJ.textSec,marginBottom:3,marginTop:8};

function StatusBadge({status}) {
  const ok = status === "Approved";
  return (
    <span style={{fontSize:10,fontWeight:700,padding:"1px 7px",borderRadius:20,
      background: ok ? BJ.greenLight : BJ.amberLight, color: ok ? "#1B5E20" : "#6D4C00"}}>
      {status}
    </span>
  );
}

// True when the canonical_title is just a title-cased echo of the document_id
// (filename-derived metadata) — showing both reads as a duplicate.
function titleEchoesId(title, id) {
  const norm = s => String(s||"").replace(/[^a-z0-9]/gi,"").toLowerCase();
  return !!title && !!id && norm(title) === norm(id);
}

// Deep-link target for a cited source: page-level (#page=N) when the backend supplies a page,
// else document-level. Returns undefined when there's no source_url (e.g. local docs not yet
// crawled from SharePoint) so the caller falls back to plain (un-linked) text.
function docHref(s) {
  if (!s || !s.source_url) return undefined;
  const page = s.page ?? s.page_start;
  return page ? `${s.source_url}#page=${encodeURIComponent(page)}` : s.source_url;
}

// Click-through document-ID link (PL decision 2.8a), used by the Sources panel below (the
// provenance header that also used it was removed 2026-10-04). Falls back to a plain
// (non-clickable) span when docHref() has no URL (e.g. a
// local doc not yet crawled from SharePoint) rather than inventing a fake link.
function SourceLink({s}) {
  const href = docHref(s);
  return href
    ? <a href={href} target="_blank" rel="noreferrer" title="Open source document"
        style={{fontWeight:600,color:BJ.blue,textDecoration:"none",
          borderBottom:`1px solid ${BJ.blueBorder}`}}>{s.document_id} ↗</a>
    : <span style={{fontWeight:600,color:BJ.blue}}>{s.document_id}</span>;
}

/* ── RICH-MEDIA PHASE 1a: the cited PDF page as an image ──
   Rendered only when the backend puts a `media` ref on a cited source (flag
   rich_media_phase1a_enabled, off by default). With no `media` field nothing here renders.
   There is no role logic here (DESIGN-03 §5.2): the server decides, and a 404 or 503 shows
   only a muted "page image unavailable" note. The client only passes back (interaction_id, ref);
   it never asks for a page. */
// Object URLs of open page images, so clear() can revoke every one of them.
const openPageImageUrls = new Set();
function revokeAllPageImages() {
  openPageImageUrls.forEach(u => URL.revokeObjectURL(u));
  openPageImageUrls.clear();
}
// Same auth headers as /query: the Entra bearer plus the X-Everest-Role header plumbing.
async function mediaHeaders(backendRole) {
  const token = await getBackendAuthToken();
  return {
    "X-Everest-Role": backendRole || "FieldOperator",
    ...(token ? {Authorization: `Bearer ${token}`} : {}),
  };
}

// AEGIS D3: nothing is fetched when the answer renders. The locate call (which makes the
// server download and parse the PDF) runs only when the user clicks the chip, so an answer
// nobody opens costs no downloads. A 404 or 503 shows a short muted note, not an error.
export function PageChip({interactionId, media, backendRole}) {
  const [loc, setLoc] = useState(null);
  const [state, setState] = useState("idle");  // idle | loading | ready | unavailable
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const onClick = async () => {
    if (state === "ready") { setOpen(true); return; }
    if (state !== "idle") return;
    setState("loading");
    try {
      const res = await fetch(
        `${BACKEND_URL}/media/answers/${encodeURIComponent(interactionId)}/${encodeURIComponent(media.ref)}`,
        {headers: await mediaHeaders(backendRole)});
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      if (!mounted.current) return;
      setLoc(body); setState("ready"); setOpen(true);
    } catch { if (mounted.current) setState("unavailable"); }
  };
  if (state === "loading") return <span style={{fontSize:11,color:BJ.textMut,fontStyle:"italic"}}>locating page…</span>;
  if (state === "unavailable") return <span style={{fontSize:11,color:BJ.textMut}}>page image unavailable</span>;
  const text = loc
    ? (loc.page_label ? `p. ${loc.page_label} (PDF p. ${loc.page})` : `PDF p. ${loc.page}`)
    : "Show cited page";
  return (
    <>
      <button type="button" className="ev-btn" onClick={onClick}
        title="Show the cited page"
        style={{fontSize:11,fontWeight:600,padding:"1px 8px",borderRadius:20,cursor:"pointer",
          background:BJ.blueLight,color:BJ.blue,border:`1px solid ${BJ.blueBorder}`}}>
        🖼 {text}
      </button>
      {open && loc && <PageViewer loc={loc} backendRole={backendRole} onClose={close}/>}
    </>
  );
}

function PageViewer({loc, backendRole, onClose}) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const closeRef = useRef(null);
  useEffect(() => {
    let active = true, url = null;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}${loc.image_path}`, {headers: await mediaHeaders(backendRole)});
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        openPageImageUrls.add(url);
        if (active) setSrc(url);
      } catch { if (active) setFailed(true); }
    })();
    // Revoked on close and on unmount (closing unmounts the viewer).
    return () => {
      active = false;
      if (url) { URL.revokeObjectURL(url); openPageImageUrls.delete(url); }
    };
  }, [loc.image_path, backendRole]);
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    if (closeRef.current) closeRef.current.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const pageText = `Page ${loc.page_label || loc.page} (PDF page ${loc.page} of ${loc.page_count})`;
  const platforms = (loc.platform_applicability && loc.platform_applicability.length)
    ? loc.platform_applicability.join(", ") : "not specified";
  return (
    <div onClick={onClose}
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:1000,
        display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div role="dialog" aria-modal="true" aria-labelledby="ev-page-viewer-title"
        onClick={e=>e.stopPropagation()}
        style={{background:BJ.card,borderRadius:12,maxWidth:"min(960px, 100%)",width:"100%",
          maxHeight:"100%",display:"flex",flexDirection:"column",overflow:"hidden",
          boxShadow:"0 10px 40px rgba(0,0,0,0.3)"}}>
        {/* Provenance header: always shown, cannot be dismissed apart from closing the viewer. */}
        <div style={{padding:"10px 14px",borderBottom:`1px solid ${BJ.border}`,background:BJ.surface}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",fontSize:13}}>
            <span id="ev-page-viewer-title" style={{fontWeight:700,color:BJ.blue}}>{loc.document_id}</span>
            <span style={{color:BJ.textSec}}>Rev {loc.revision}</span>
            <StatusBadge status={loc.status}/>
            <span style={{fontSize:10,fontWeight:700,padding:"1px 7px",borderRadius:20,
              background:BJ.blueLight,color:BJ.blueDark}}>
              SME validation: {loc.sme_validation_status}
            </span>
            <span style={{flex:1}}/>
            <button ref={closeRef} type="button" className="ev-btn" onClick={onClose}
              aria-label="Close page viewer"
              style={{background:"#fff",border:`1px solid ${BJ.borderMid}`,borderRadius:8,
                padding:"3px 10px",fontSize:12,cursor:"pointer",color:BJ.textPri}}>✕ Close</button>
          </div>
          <div style={{fontSize:12,color:BJ.textSec,marginTop:4}}>
            {pageText} · Platform applicability: {platforms}
          </div>
          <div style={{fontSize:12,color:BJ.textSec,marginTop:4}}>
            Rendered from the SharePoint file that matches the cited revision. Verify against the
            controlled copy before acting.
          </div>
          {loc.document_type === "Schematic" && (
            <div style={{fontSize:12,fontWeight:600,marginTop:6,padding:"5px 8px",borderRadius:6,
              background:BJ.amberLight,color:"#6D4C00"}}>
              Use this drawing to locate components, not to diagnose. Confirm against the
              controlled drawing.
            </div>
          )}
        </div>
        <div style={{flex:1,overflow:"auto",padding:10,background:BJ.surface2,textAlign:"center"}}>
          {failed
            ? <div style={{padding:24,fontSize:13,color:BJ.textSec}}>Page image unavailable.</div>
            : src
              ? <img src={src} alt={`${loc.document_id} ${loc.canonical_title}, page ${loc.page_label || loc.page}`}
                  style={{maxWidth:"100%",height:"auto",background:"#fff"}}/>
              : <div style={{padding:24,fontSize:13,color:BJ.textMut}}>Loading page…</div>}
        </div>
        {loc.source_url && (
          <div style={{padding:"8px 14px",borderTop:`1px solid ${BJ.border}`,fontSize:12}}>
            <a href={`${loc.source_url}#page=${encodeURIComponent(loc.page)}`} target="_blank" rel="noreferrer"
              style={{color:BJ.blue,fontWeight:600}}>
              Open current file in SharePoint at page {loc.page} ↗
            </a>
            <div style={{fontSize:11,color:BJ.textMut,marginTop:2}}>
              Page linking is not verified for every library; the current file may differ from the
              cited revision.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// The platform renders the structured, hyperlinked citation panel (with page numbers)
// below every answer, so strip any model-emitted trailing "Sources:" / "📎 Sources:" list
// to avoid a duplicate section. Anchored to the end so inline "(ETM-09)" citations stay.
function stripTrailingSources(text) {
  return String(text || "").replace(/\n+\s*(?:📎\s*)?#{0,4}\s*sources?\s*:[\s\S]*$/i, "").trimEnd();
}

// PROP-017 C-8 (multi-turn context). All three readers are defensive: with the backend flag
// multiturn_context_enabled off, `turn_context` is null (PROP-017 §6.3) and each returns the
// pre-C-8 answer (no anchor, no notice, not a TCR clarification).
// Anchor eligibility (§4.1): only an explicit `true` counts; absent or anything else is not eligible.
function isAnchorEligible(resp) {
  return !!(resp && resp.turn_context && resp.turn_context.anchor_eligible === true);
}
// The answer was grounded on the previous question's sources: RESTATE (§4.2 row 3) or an
// ANAPHORIC turn whose drift guard tripped to REUSE (§5).
function reusedPriorSources(resp) {
  const tc = (resp && resp.turn_context) || {};
  // Notice-only. The backend sets `notice` on every ANSWERED turn grounded on the anchor's
  // sources (RESTATE, or ANAPHORIC tripped to REUSE; api/query.py), and on nothing else.
  // It never sends turn_class "REUSE" or a drift_guard block, and an answered RESTATE
  // always carries the notice, so no turn_class fallback is needed.
  return typeof tc.notice === "string" && !!tc.notice.trim();
}
// A Turn Context Resolver clarification (DEGENERATE under D-4 A, or the D11 drift CLARIFY):
// the backend sets turn_context.clarify_reason (§5). It has Edit only, no Confirm.
function tcrClarifyReason(resp) {
  const tc = resp && resp.turn_context;
  return (tc && tc.clarify_reason) || null;
}

function BackendAnswer({resp, onFeedback, feedbackSent, backendRole}) {
  // "What went wrong?" sheet state (Learning Hub Increment 1). Only relevant for negative
  // reactions — "correct" submits immediately, same as before. Local to this card; unmounts
  // once feedbackSent flips true (thank-you state), matching the existing 3-reaction behavior.
  const [pendingReaction, setPendingReaction] = useState(null);
  const [category, setCategory] = useState("other");
  const [expectedAnswer, setExpectedAnswer] = useState("");
  const [sourceInput, setSourceInput] = useState("");
  const [comment, setComment] = useState("");

  function pickReaction(r) {
    if (r === "correct") { onFeedback({reaction:"correct"}); return; }
    setPendingReaction(r);
    setCategory(defaultCategoryFor(r));
  }
  function submitSheet() {
    const src = sourceInput.trim();
    const isUrl = /^https?:\/\//i.test(src);
    onFeedback({
      reaction: pendingReaction,
      issue_category: category || undefined,
      expected_correct_answer: expectedAnswer.trim() || undefined,
      correct_source_document_id: (src && !isUrl) ? src : undefined,
      correct_source_url: (src && isUrl) ? src : undefined,
      user_comment: comment.trim() || undefined,
    });
  }
  function skipSheet() { onFeedback({reaction: pendingReaction}); }

  const g = resp.guardrail || {};
  // g.confidence_band is still returned, logged and exported — it is simply no longer
  // rendered (PL decision 2.8a; see the comment above the LOW-band caveat below).
  const sources = resp.cited_sources || [];
  const refused = g.refused;
  const genError = !resp.answer && g.action === "generation_error";
  // PROP-013 §7.1/§7.2: ABSTAIN arrives with refused=false and escalated=true (§4.1), so it
  // is excluded from `suppressed` to get its own banner instead of the LOW-band one.
  const abstain = !resp.answer && g.action === "abstain";
  const suppressed = !genError && !abstain && !resp.answer && (g.escalated || g.action === "suppress_and_escalate");
  // ev-14 a (PROP-013 §7.3): a decline — any response with no answer text — shows the plain
  // banner only, for every role. The backend already sends cited_sources=[] then; this is
  // defence in depth against a stale or hand-built response. Diagnostics stay in the log.
  const hasAnswer = !!(resp.answer && String(resp.answer).trim());
  // Small-talk reply (backend llm/smalltalk.py, PL 2026-09-25): a plain chat bubble. No
  // retrieval ran, so there is no verify line, LOW caveat, reuse
  // notice or Sources panel; the feedback buttons stay.
  const conversational = hasAnswer && resp.answer_mode === "conversational";

  // Referenced/Context split (Phase 2 WS-1; PROP-013 §8): documents the model cited
  // (`cited === true`) are "Referenced"; other retrieved docs are demoted to "Additional
  // context". Only an older response with no `cited` field at all keeps everything in the
  // primary list (fallback); a response that carries `cited` is never counted past it (FC-1).
  let referenced = [], context = [];
  sources.forEach(s => {
    (s.cited === true ? referenced : context).push(s);
  });
  const hasCitedField = sources.some(s => s && "cited" in s);
  if (!hasCitedField) { referenced = sources; context = []; }

  // PROP-017 §5 REUSE row: the reused-sources notice, shown on every RESTATE and REUSE
  // answer. Answers only (hasAnswer): a decline keeps its plain banner with no sources
  // (ev-14 a). The titles are the reused set the backend returned as cited_sources.
  const reuseTitles = (hasAnswer && !conversational && reusedPriorSources(resp))
    ? [...new Set(sources.map(s => (s && (s.canonical_title || s.document_id)) || "").filter(Boolean))]
    : null;

  return (
    <div>
      {refused && (
        <div style={{background:BJ.redLight,border:`1px solid ${BJ.red}`,borderRadius:10,
          padding:"10px 13px",marginBottom:8,
          animation:"evPop var(--ev-dur-base) var(--ev-ease-out) both"}}>
          <div style={{fontWeight:700,color:BJ.red,marginBottom:2}}>⛔ Not answered</div>
          {/* FORGE P3: plain wording for users; the engine's reason codes and rule id are
              kept only in title/data attributes for support (they stay in the log too). */}
          <div style={{fontSize:13,color:BJ.textSec,lineHeight:1.5}}
            title={[g.refusal_rule_id && `rule ${g.refusal_rule_id}`, ...(g.reasons || [])].filter(Boolean).join(" · ") || undefined}
            data-refusal-rule={g.refusal_rule_id || undefined}
            data-refusal-reasons={(g.reasons && g.reasons.join(" ")) || undefined}>
            This request was refused by the governed-answer policy.
          </div>
        </div>
      )}

      {resp.answer && (
        <>
          {/* PROP-017 reused-sources notice (copy verbatim from §5, REUSE row). Always
              rendered in full: no toggle, no collapse, no hover-only text (§12). It sits
              above the B6.26 LOW caveat so that caveat stays directly above the answer
              text. */}
          {reuseTitles && (
            <div role="note" data-turn-context="reused-sources"
              style={{marginBottom:8,padding:"8px 12px",borderRadius:8,
                background:BJ.surface,border:`1px solid ${BJ.borderMid}`,
                borderLeft:`4px solid ${BJ.blue}`,fontSize:13,color:BJ.textPri,lineHeight:1.5}}>
              {(resp.turn_context && typeof resp.turn_context.notice === "string" && resp.turn_context.notice.trim())
                ? resp.turn_context.notice
                : <>
                    {reuseTitles.length > 0
                      ? <>Answered from the sources of your previous question: <b>{reuseTitles.join("; ")}</b>.</>
                      : <>Answered from the sources of your previous question.</>}
                    {" "}If you meant something new, ask it as a full question.
                  </>}
            </div>
          )}
          {/* No confidence chip and no provenance box above the answer.
              - The confidence chip stays hidden (PL ruling 2.8a). The chip displayed a band
                the confidence formula cannot compute honestly: it consumes an Azure
                rank-fusion score (ceiling ~0.033) as if it were a 0-to-1 similarity, so
                "High" is mathematically unreachable in production. An unvalidated
                indicator in front of safety-critical users is worse than none. Display
                layer only: the engine still computes, logs and exports the score (Phase B
                baseline and Phase C recalibration need it). SUNSET: the chip (or a
                successor band) returns when a Phase C re-baseline demonstrates band
                accuracy, or when PROP-016 ships — never on a bare date.
              - The blue "Grounded in N controlled document(s)" provenance box that replaced
                the chip was itself removed by PL directive 2026-10-04: it repeated the
                documents already listed in the Sources panel below the answer.
              - Its verify sentence ("The controlled document governs — verify against the
                source before acting.") moved into the Sources panel, directly under the
                "📎 Sources (n)" heading, to keep PL lock ev-16 b (verify wording on all
                answers). Same gating as the box: an answer with at least one referenced
                (cited) document, so it never claims "the controlled document governs"
                when nothing was cited. */}
          {!conversational && g.confidence_band === "low" && (
            <div role="alert" style={{background:BJ.amberLight,border:"1px solid #FFD180",borderRadius:10,
              padding:"8px 13px",marginBottom:8,fontSize:13,color:"#6D4C00",
              animation:"evPop var(--ev-dur-base) var(--ev-ease-out) both"}}>
              ⚠️ <b>{LOW_BAND_CAVEAT}</b>
            </div>
          )}
          <div className="ev-rich" style={{fontSize:14,lineHeight:1.7}} dangerouslySetInnerHTML={{__html:fmt(stripTrailingSources(resp.answer))}}/>
          {resp.truncated && (
            <div style={{background:BJ.amberLight,border:"1px solid #FFD180",borderRadius:10,
              padding:"8px 13px",marginTop:8,fontSize:13,color:"#6D4C00",
              animation:"evPop var(--ev-dur-base) var(--ev-ease-out) both"}}>
              ✂️ <b>This answer was cut off at the response length limit.</b> Ask a
              follow-up (e.g. "continue from where you stopped") for the remainder.
            </div>
          )}
        </>
      )}

      {genError && (
        <div style={{background:BJ.redLight,border:`1px solid ${BJ.red}`,borderRadius:10,
          padding:"10px 13px",marginTop:0,
          animation:"evPop var(--ev-dur-base) var(--ev-ease-out) both"}}>
          <div style={{fontWeight:700,color:BJ.red,marginBottom:2}}>⚙️ System issue — no answer was generated</div>
          <div style={{fontSize:13,color:BJ.textSec,lineHeight:1.5}}>
            Everest couldn't generate an answer just now. <b>This is a system problem, not a
            gap in the Knowledge Library</b> — the relevant documents were found. Please try
            again in a moment; if this keeps happening, notify the Everest team.
          </div>
        </div>
      )}

      {suppressed && (
        <div style={{background:BJ.amberLight,border:"1px solid #FFD180",borderRadius:10,
          padding:"10px 13px",marginTop:resp.answer?8:0,
          animation:"evPop var(--ev-dur-base) var(--ev-ease-out) both"}}>
          <div style={{fontWeight:700,color:"#6D4C00",marginBottom:2}}>⚠️ Not confident enough to answer</div>
          <div style={{fontSize:13,color:BJ.textSec,lineHeight:1.5}}>
            Everest didn't find a strong enough match in the Knowledge Library to answer safely.
            {" "}Escalate through your normal path:
          </div>
          <ul style={{margin:"6px 0 0",paddingLeft:18,fontSize:13,color:BJ.textSec}}>
            <li><b>Equipment / technical:</b> Field Leader or Maintenance → the TITAN or Truduo SME → Equipment Engineering</li>
            <li><b>Operations:</b> Field Leader → Operations Supervisor → Frac Manager</li>
          </ul>
        </div>
      )}

      {/* PROP-013 §7.1 / §7.2 abstention banners (copy verbatim). No chip, verify line
          or Sources panel (§7.3). */}
      {abstain && (
        <div role="status" style={{background:BJ.amberLight,border:"1px solid #FFD180",borderRadius:10,
          padding:"10px 13px",marginTop:0,
          animation:"evPop var(--ev-dur-base) var(--ev-ease-out) both"}}>
          {g.abstain_stage === "post_generation_uncited" ? (
            <>
              <div style={{fontWeight:700,color:"#6D4C00",marginBottom:2}}>Not answered — no approved source supported an answer</div>
              <div style={{fontSize:13,color:BJ.textSec,lineHeight:1.5}}>
                Everest found related documents but could not tie an answer to an approved source, so no answer is shown.
                {" "}Escalate through your normal path:
              </div>
            </>
          ) : (
            <>
              <div style={{fontWeight:700,color:"#6D4C00",marginBottom:2}}>Not found in the Knowledge Library</div>
              <div style={{fontSize:13,color:BJ.textSec,lineHeight:1.5}}>
                Everest searched the controlled documents available to your role and did not find one that addresses this question closely enough to answer from. It has not guessed.
                {" "}Escalate through your normal path:
              </div>
            </>
          )}
          <ul style={{margin:"6px 0 0",paddingLeft:18,fontSize:13,color:BJ.textSec}}>
            <li><b>Equipment / technical:</b> Field Leader or Maintenance → the TITAN or Truduo SME → Equipment Engineering</li>
            <li><b>Operations:</b> Field Leader → Operations Supervisor → Frac Manager</li>
          </ul>
          {g.abstain_stage !== "post_generation_uncited" && (
            <div style={{fontSize:13,color:BJ.textSec,lineHeight:1.5,marginTop:6}}>
              If you know a controlled document that covers this, report it as a missing document.
            </div>
          )}
        </div>
      )}

      {/* ev-14 a: Sources panel only when there is an answer (PROP-013 §19 item 5). */}
      {hasAnswer && !conversational && sources.length>0 && (
        <div style={{marginTop:10,borderTop:`1px solid ${BJ.border}`,paddingTop:8,
          animation:"evFade var(--ev-dur-base) var(--ev-ease-out) both"}}>
          <div style={{fontSize:11,fontWeight:700,color:BJ.textSec,textTransform:"uppercase",
            letterSpacing:.5,marginBottom:6}}>📎 Sources ({referenced.length})</div>
          {/* ev-16 b verify line, moved here from the removed provenance box (PL directive
              2026-10-04). Plain muted text, no box; only when something was cited. */}
          {referenced.length > 0 && (
            <div style={{fontSize:11.5,color:BJ.textMut,marginTop:-2,marginBottom:6}}>
              The controlled document governs — verify against the source before acting.
            </div>
          )}
          {referenced.map((s,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",
              marginBottom:5,fontSize:13}}>
              <SourceLink s={s}/>
              {s.revision && <span style={{fontSize:11,color:BJ.textMut}}>Rev {s.revision}</span>}
              {(s.page ?? s.page_start) != null && (
                <span style={{fontSize:11,color:BJ.textMut}}>p. {s.page ?? s.page_start}</span>
              )}
              <StatusBadge status={s.status}/>
              {hasAnswer && s.media && resp.interaction_id && (
                <PageChip interactionId={resp.interaction_id} media={s.media} backendRole={backendRole}/>
              )}
              {(s.chunks_matched||1) > 1 && (
                <span style={{fontSize:11,color:BJ.textMut}}>{s.chunks_matched} sections matched</span>
              )}
              {!titleEchoesId(s.canonical_title, s.document_id) && (
                <span style={{color:BJ.textSec}}>{s.canonical_title}</span>
              )}
            </div>
          ))}
          {context.length>0 && (
            <div style={{marginTop:6,fontSize:11.5,color:BJ.textMut,lineHeight:1.6}}>
              Additional context retrieved (not cited in this answer):{" "}
              {context.map((s,i)=>(
                <span key={i}>
                  {i>0 && " · "}
                  {s.document_id}{s.revision ? ` (Rev ${s.revision})` : ""}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* No feedback prompt on a generation error. Asking "was this helpful?" about an
          answer that was never generated invites reactions like "incomplete" or
          "wrong_source" against a system fault, which would land in feedback_records as
          answer-quality signal and pollute the very flywheel this data feeds. The red
          system-issue banner above already tells the user what happened. */}
      {!genError && (
        <div style={{marginTop:10,display:"flex",alignItems:"center",gap:8,fontSize:12,
          color:BJ.textMut,flexWrap:"wrap"}}>
          {feedbackSent ? <span>✓ Thanks — feedback recorded.</span> : (
            <>
              <span>Was this helpful?</span>
              {REACTIONS.map(r=>(
                <button key={r.v} type="button" className="ev-btn"
                  onClick={()=>pickReaction(r.v)}
                  aria-pressed={pendingReaction===r.v}
                  aria-label={r.label.replace(/^\S+\s/,"")}
                  style={fbChipStyle(r.v, pendingReaction===r.v)}>{r.label}</button>
              ))}
            </>
          )}
        </div>
      )}

      {/* "What went wrong?" sheet — expands below the reaction row for any negative reaction.
          Never obscures the answer/citations/safety notices above; Skip stays backward-compatible
          with a bare-reaction POST /feedback. */}
      {!feedbackSent && pendingReaction && (
        <div style={{marginTop:8,padding:"10px 12px",borderRadius:10,
          background: pendingReaction==="unsafe" ? BJ.redLight : BJ.surface,
          border:`1px solid ${pendingReaction==="unsafe" ? "#EE0000" : BJ.borderMid}`,
          animation:"evPop var(--ev-dur-base) var(--ev-ease-out) both"}}>
          <div style={{fontWeight:700,fontSize:13,marginBottom:2,
            color: pendingReaction==="unsafe" ? "#EE0000" : BJ.textPri}}>
            {pendingReaction==="unsafe" ? "⚠ Unsafe answer — what went wrong?" : "What went wrong?"}
          </div>

          <label style={fbLabel} htmlFor={`fb-cat-${resp.interaction_id}`}>Category</label>
          <select id={`fb-cat-${resp.interaction_id}`} style={fbInput}
            value={category} onChange={e=>setCategory(e.target.value)}>
            {ISSUE_CATEGORIES.map(c=>(<option key={c.v} value={c.v}>{c.label}</option>))}
          </select>

          <label style={fbLabel} htmlFor={`fb-exp-${resp.interaction_id}`}>What should the answer have been?</label>
          <textarea id={`fb-exp-${resp.interaction_id}`} style={{...fbInput,resize:"vertical"}} rows={2}
            placeholder="Optional" value={expectedAnswer} onChange={e=>setExpectedAnswer(e.target.value)}/>

          <label style={fbLabel} htmlFor={`fb-src-${resp.interaction_id}`}>Correct source (doc # or link, if known)</label>
          <input id={`fb-src-${resp.interaction_id}`} type="text" style={fbInput}
            placeholder="e.g. ETM-09 or https://…" value={sourceInput} onChange={e=>setSourceInput(e.target.value)}/>

          <label style={fbLabel} htmlFor={`fb-cmt-${resp.interaction_id}`}>Additional comments (optional)</label>
          <textarea id={`fb-cmt-${resp.interaction_id}`} style={{...fbInput,resize:"vertical"}} rows={2}
            placeholder="Optional" value={comment} onChange={e=>setComment(e.target.value)}/>

          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10}}>
            <button type="button" className="ev-btn" onClick={submitSheet}
              style={{background:BJ.blue,color:"#fff",border:"none",borderRadius:8,
                padding:"7px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
              Submit feedback
            </button>
            <button type="button" className="ev-btn" onClick={skipSheet}
              style={{background:"transparent",color:BJ.textSec,border:`1px solid ${BJ.borderMid}`,
                borderRadius:8,padding:"7px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
              Skip — just send reaction
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
 
/* ── SSO SIGN-IN SPLASH ── */
// Shown while MSAL redirects to BJ Entra ID (auto-triggered in App). When a sign-in returned
// an Entra error (declined consent, conditional-access block, interrupted MFA), shows the
// error + a Retry button INSTEAD of auto-bouncing — otherwise a non-recoverable failure would
// loop back to Entra forever.
function SignInSplash({authError}) {
  const errText = authError && (authError.errorMessage || authError.message || String(authError));
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,28,56,0.68)",
      backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",
      zIndex:1000,padding:20,fontFamily:"'Barlow',system-ui,sans-serif"}}>
      <div style={{background:BJ.card,borderRadius:18,width:"100%",maxWidth:420,
        boxShadow:"0 12px 48px rgba(0,0,0,.22)",overflow:"hidden"}}>
        <div style={{background:BJ.blue,padding:"22px 26px",display:"flex",alignItems:"center",gap:14}}>
          <BJLogo size={38}/>
          <div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:700,color:"#fff",lineHeight:1.1}}>Project Everest</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,.5)",letterSpacing:1,textTransform:"uppercase"}}>Equipment Intelligence Platform</div>
          </div>
        </div>
        <div style={{padding:"26px",textAlign:"center"}}>
          {errText ? (
            <div style={{background:BJ.redLight,border:`1px solid ${BJ.red}`,borderRadius:10,
              padding:"10px 13px",marginBottom:18,textAlign:"left"}}>
              <div style={{fontWeight:700,color:BJ.red,marginBottom:3}}>⛔ Sign-in didn't complete</div>
              <div style={{fontSize:12,color:BJ.textSec,lineHeight:1.5,wordBreak:"break-word"}}>{errText}</div>
            </div>
          ) : (
            <div style={{fontSize:14,color:BJ.textSec,lineHeight:1.6,marginBottom:20}}>
              🔒 Sign in with your BJ Energy account to access the governed Knowledge Hub.
            </div>
          )}
          <button onClick={()=>signIn()}
            style={{padding:"12px 28px",background:BJ.blue,color:"#fff",border:"none",
              borderRadius:10,fontFamily:"inherit",fontSize:14,fontWeight:600,cursor:"pointer"}}>
            {errText ? "Try again →" : "Sign in with BJ Energy →"}
          </button>
          {!errText && (
            <p style={{color:BJ.textMut,fontSize:11,marginTop:16}}>
              Redirecting to Microsoft sign-in… If nothing happens, click the button above.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── SETUP / LOGIN SCREEN ── */
function Setup({onLaunch, defaultName=""}) {
  const [name,    setName]    = useState(defaultName);
  const [role,    setRole]    = useState("");
  const [apiKey,  setApiKey]  = useState("");
 
  const canLaunch = USE_BACKEND ? (name.trim() && role) : (name.trim() && role && apiKey.trim());

  const go = () => {
    if (!canLaunch) return;
    onLaunch(name.trim(), role, USE_BACKEND ? "" : apiKey.trim());
  };
 
  const inp = {
    width:"100%", border:`1.5px solid ${BJ.borderMid}`, borderRadius:10,
    padding:"11px 13px", fontFamily:"inherit", fontSize:14,
    color:BJ.textPri, outline:"none", background:BJ.card, boxSizing:"border-box",
  };
 
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,28,56,0.68)",
      backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",
      zIndex:1000,padding:20,fontFamily:"'Barlow',system-ui,sans-serif"}}>
 
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow:ital,wght@0,300;0,400;0,500;0,600;0,700;1,700&family=Barlow+Condensed:ital,wght@0,600;0,700;1,700&display=swap');
        @keyframes slideUp{from{transform:scale(.96) translateY(12px);opacity:0}to{transform:scale(1) translateY(0);opacity:1}}
        @keyframes bn{0%,80%,100%{transform:scale(.6);opacity:.3}40%{transform:scale(1);opacity:1}}
        *{box-sizing:border-box}
        pre{background:#F5F6F8;border:1px solid rgba(0,0,0,.1);border-radius:8px;padding:10px 12px;font-size:12px;overflow-x:auto;margin:8px 0;white-space:pre-wrap;word-break:break-word}
        code{background:#F5F6F8;padding:1px 5px;border-radius:4px;font-size:12px}
        ul{padding-left:18px;margin:4px 0}li{margin-bottom:2px}
        h2,h3,h4{color:#00427B;margin:8px 0 3px}p{margin:3px 0}b{font-weight:600}
        ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:3px}
      `}</style>
 
      <div style={{background:BJ.card,borderRadius:18,width:"100%",maxWidth:480,
        boxShadow:"0 12px 48px rgba(0,0,0,.22)",overflow:"hidden",
        animation:"slideUp .28s cubic-bezier(.22,1,.36,1)"}}>
 
        {/* Header */}
        <div style={{background:BJ.blue,padding:"22px 26px",display:"flex",alignItems:"center",gap:14}}>
          <BJLogo size={38}/>
          <div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:700,color:"#fff",lineHeight:1.1}}>Project Everest</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,.5)",letterSpacing:1,textTransform:"uppercase"}}>Equipment Intelligence Platform</div>
          </div>
        </div>
 
        {/* Body */}
        <div style={{padding:"22px 26px 24px"}}>
          <div style={{background:BJ.amberLight,border:"1px solid #FFD180",borderRadius:8,
            padding:"9px 12px",fontSize:12,color:"#6D4C00",lineHeight:1.5,marginBottom:18,display:"flex",gap:8}}>
            <span>⚠</span>
            <span>Verify all safety-critical actions with your SME before acting. This platform is for reference only.</span>
          </div>
 
          {/* Name */}
          <div style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:11,fontWeight:600,color:BJ.textSec,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Your Name</label>
            <input style={inp} value={name} placeholder="e.g. Dylan M."
              onChange={e=>setName(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&document.getElementById("role-select")?.focus()}/>
          </div>
 
          {/* Role */}
          <div style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:11,fontWeight:600,color:BJ.textSec,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Role</label>
            <select id="role-select" style={inp} value={role} onChange={e=>setRole(e.target.value)}>
              <option value="">Select your role…</option>
              {ROLES.map(r=><option key={r.value} value={r.value}>{r.value}</option>)}
            </select>
          </div>
 
          {/* API Key — only in the legacy direct-to-Anthropic mode. With the governed
              backend (USE_BACKEND), identity comes from BJ SSO and no key is entered. */}
          {USE_BACKEND ? (
            <div style={{marginBottom:20,background:BJ.blueLight,border:`1px solid ${BJ.blueBorder}`,
              borderRadius:10,padding:"10px 12px",fontSize:12,color:BJ.textSec,lineHeight:1.5}}>
              🔒 Secured by BJ single sign-on. Answers are served from the governed Knowledge
              Library — no API key required.
            </div>
          ) : (
            <div style={{marginBottom:20}}>
              <label style={{display:"block",fontSize:11,fontWeight:600,color:BJ.textSec,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Anthropic API Key</label>
              <input style={inp} type="password" value={apiKey} placeholder="sk-ant-…"
                onChange={e=>setApiKey(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&canLaunch&&go()}/>
              <div style={{fontSize:11,color:BJ.textMut,marginTop:4}}>
                Stored in session memory only. Not saved to disk or transmitted anywhere other than the Anthropic API.
              </div>
            </div>
          )}
 
          {/* Launch */}
          <div style={{display:"flex",justifyContent:"flex-end"}}>
            <button onClick={go} disabled={!canLaunch}
              style={{padding:"11px 28px",
                background:canLaunch?BJ.blue:BJ.surface2,
                color:canLaunch?"#fff":BJ.textMut,
                border:"none",borderRadius:10,fontFamily:"inherit",
                fontSize:14,fontWeight:600,cursor:canLaunch?"pointer":"not-allowed",
                transition:"background .15s"}}>
              Launch Platform →
            </button>
          </div>
 
          <p style={{textAlign:"center",color:BJ.textMut,fontSize:11,marginTop:12}}>
            Project Everest v0.1 · BJ Energy Solutions · Confidential
          </p>
        </div>
      </div>
    </div>
  );
}
 
/* ── MAIN APP ── */
export default function App() {
  // BJ SSO (MSAL) account; set at init in index.js before first render. null = not signed in.
  const [account,   setAccount]   = useState(() => getActiveAccount());
  const [launched,  setLaunched]  = useState(false);
  const [userName,  setUserName]  = useState("");
  const [userRole,  setUserRole]  = useState("");
  const [apiKey,    setApiKey]    = useState("");
  const [depth,     setDepth]     = useState("basic");
  const [depthOpen, setDepthOpen] = useState(false);
  const [messages,  setMessages]  = useState([]);
  const [input,     setInput]     = useState("");
  const [loading,   setLoading]   = useState(false);
  const [history,   setHistory]   = useState([]);
  const [msgCount,  setMsgCount]  = useState(0);
  const [convId,    setConvId]    = useState(() => "C" + Date.now().toString(36).toUpperCase());
  const [health,    setHealth]    = useState(undefined); // undefined=checking · null=unreachable · obj=/health/status
  const [statusOpen,setStatusOpen]= useState(false);
  const [typeCounts,setTypeCounts]= useState(null); // KB per-type doc counts (IT + Project Lead only)

  const chatRef  = useRef(null);
  const inputRef = useRef(null);
  const redirectAttempted = useRef(false);  // one-shot guard on the SSO auto-redirect
  // PROP-017 §4.1: interaction_id of the most recent answer whose response had
  // turn_context.anchor_eligible === true; sent as prior_interaction_id. null = send none.
  const anchorIdRef = useRef(null);

  const scroll = () => setTimeout(()=>chatRef.current?.scrollTo({top:chatRef.current.scrollHeight,behavior:"smooth"}),30);

  /* ── BJ SSO GATE — require an Entra sign-in before the app loads (governed mode) ── */
  // Keep `account` in sync with MSAL across sign-in / token / sign-out events, not just the
  // value captured at first render.
  useEffect(() => subscribeAccount(setAccount), []);

  // Inject the persistent global stylesheet once (fonts, keyframes, scoped answer formatting).
  // This is what un-freezes the "Thinking…" dots and indents answer bullets in the chat view —
  // the (unmounting) Setup <style> no longer covers it (and is gone entirely in governed mode).
  useEffect(() => {
    if (document.getElementById("everest-global-style")) return;
    const el = document.createElement("style");
    el.id = "everest-global-style";
    el.textContent = GLOBAL_CSS;
    document.head.appendChild(el);
  }, []);

  // Governed mode: identity comes from BJ SSO. Derive the display name + role from the signed-in
  // Entra account (the ID token's `roles` claim — the SAME claim the backend authorizes on) and
  // enter the app directly. No manual name entry, no role dropdown (roles are defined in Azure).
  // Role here is display/prompt-only; the backend independently enforces role from the token.
  useEffect(() => {
    if (!USE_BACKEND || !account) return;
    setUserName(account.name || account.username || "BJ Energy user");
    const claimRoles = (account.idTokenClaims && account.idTokenClaims.roles) || [];
    const mapped = claimRoles.map(r => BACKEND_TO_ROLE[r]).find(Boolean);
    // Never silently downgrade an unmapped role to the lowest privilege — show the raw claim
    // if we don't have a label for it. (Backend still enforces real authz from the token.)
    setUserRole(mapped || claimRoles[0] || "Field Operator");
    setLaunched(true);
  }, [account]);

  // When the governed backend is in use and no one is signed in, redirect to BJ Entra ID —
  // but only ONCE per page life, and never when a prior sign-in returned an error (otherwise a
  // non-recoverable failure, e.g. a conditional-access block, would loop straight back to
  // Entra). The error case shows a Retry surface instead (SignInSplash).
  useEffect(() => {
    if (!USE_BACKEND || account) return;
    if (getAuthError() || redirectAttempted.current) return;
    redirectAttempted.current = true;
    signIn();
  }, [account]);

  /* ── BACKEND READINESS POLL (R2) — independent of the chat path ── */
  useEffect(() => {
    if (!launched) return;
    if (!BACKEND_URL) { setHealth(null); return; }  // not wired yet → honestly "unreachable"
    let active = true;
    const poll = async () => {
      const ctrl = new AbortController();
      const timer = setTimeout(()=>ctrl.abort(), 8000);
      try {
        const res = await fetch(`${BACKEND_URL}/health/status`, {signal:ctrl.signal});
        const data = res.ok ? await res.json() : null;
        if (active) setHealth(data);
      } catch {
        if (active) setHealth(null);
      } finally {
        clearTimeout(timer);
      }
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => { active = false; clearInterval(id); };
  }, [launched]);

  /* ── KB PER-TYPE COMPOSITION (IT + Project Lead only) — role-gated corpus breakdown ── */
  useEffect(() => {
    if (!launched || !USE_BACKEND || !BACKEND_URL) return;
    const backendRole = ROLE_TO_BACKEND[userRole];
    if (backendRole !== "IT" && backendRole !== "ProjectLead") { setTypeCounts(null); return; }
    let active = true;
    const poll = async () => {
      try {
        const token = await getBackendAuthToken();
        const res = await fetch(`${BACKEND_URL}/admin/ingest/type-counts`, {
          headers: {
            "X-Everest-Role": backendRole,
            ...(token ? {Authorization: `Bearer ${token}`} : {}),
          },
        });
        if (active && res.ok) setTypeCounts(await res.json());
      } catch { /* keep prior value; the /health poll drives the top-level indicator */ }
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { active = false; clearInterval(id); };
  }, [launched, userRole]);
 
  const roleData  = ROLES.find(r=>r.value===userRole) || ROLES[0];
  const depthOpt  = DEPTHS.find(d=>d.key===depth);
  const initial   = userName.charAt(0).toUpperCase();
  const hour      = new Date().getHours();
  const greeting  = hour<12?"Good morning":hour<17?"Good afternoon":"Good evening";
 
  /* ── SEND ── */
  const send = useCallback(async (override, opts = {}) => {
    const text = (override || input).trim();
    if (!text || loading) return;
    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
 
    const userMsg = {id:Date.now(), who:"user", text};
    const newHist = [...history, {role:"user", content:text}];
    setMessages(p=>[...p, userMsg]);
    setHistory(newHist);
    setMsgCount(c=>c+1);
    setLoading(true);
    scroll();

    // ── Governed backend path (flag-gated; OFF until cutover at gate J3/J4) ──
    if (USE_BACKEND) {
      try {
        const token = await getBackendAuthToken();
        const res = await fetch(`${BACKEND_URL}/query`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Everest-Role": ROLE_TO_BACKEND[userRole] || "FieldOperator",
            ...(token ? {Authorization: `Bearer ${token}`} : {}),
          },
          body: JSON.stringify({
            query: text,
            general_knowledge_mode: false,
            // True when the user confirmed the shorthand readback (PL 2026-07-07).
            confirmed_interpretation: !!opts.confirmed,
            // Prior turns only (the server appends the current question itself); cap to
            // the last 16 messages = 8 turns and content length, matching the backend bound.
            history: history.slice(-16).map(h=>({role:h.role, content:(h.content||"").slice(0,8000)})),
            // PROP-017 §4.1: omitted entirely when there is no eligible prior answer.
            ...(anchorIdRef.current ? {prior_interaction_id: anchorIdRef.current} : {}),
          }),
        });
        if (!res.ok) {
          let m = `Backend error ${res.status}`;
          if (res.status === 401) m = "Not signed in to the Knowledge Hub — BJ SSO sign-in required.";
          try { const e = await res.json(); if (typeof e.detail === "string") m = e.detail; } catch(_) {}
          throw new Error(m);
        }
        const data = await res.json();
        // PROP-017 §4.1 anchor selection. An eligible turn becomes the anchor. A turn that is
        // not eligible (DEGENERATE, either clarification, refused/suppressed/escalated, no
        // answer) is skipped and the earlier anchor is kept (U-10 T3). A response with no
        // turn_context at all (flag off, §6.3) clears it, so no prior_interaction_id is sent.
        if (!data.turn_context) anchorIdRef.current = null;
        else if (isAnchorEligible(data) && data.answer && data.interaction_id) anchorIdRef.current = data.interaction_id;
        // Confirm-before-search readback: no retrieval ran; show the interpretation
        // and let the user confirm or rephrase (incident CMRAOTSZI, PL 2026-07-07).
        // A TCR clarification (PROP-017 §5) uses the same needs_clarification shape and is
        // routed here too; the bubble tells them apart by turn_context.clarify_reason.
        if (data.needs_clarification && (data.interpretation || tcrClarifyReason(data))) {
          setMessages(p=>[...p, {id:Date.now()+1, who:"ai", kind:"clarify", resp:data, originalText:text}]);
          setMsgCount(c=>c+1);
          setLoading(false);
          scroll();
          inputRef.current?.focus();
          return;
        }
        setMessages(p=>[...p, {id:Date.now()+1, who:"ai", kind:"backend", resp:data}]);
        // Append the assistant turn so the next query carries real context (only when an
        // answer was returned — refused/suppressed turns leave just the user turn).
        // A small-talk turn (answer_mode "conversational") is kept out of the history
        // entirely: the user turn added above is rolled back and no assistant turn is added.
        if (data.answer_mode === "conversational") {
          setHistory(history);
        } else if (data.answer) {
          const updated = [...newHist, {role:"assistant", content:String(data.answer).slice(0,8000)}];
          setHistory(updated.length > 40 ? updated.slice(-40) : updated);
        }
        setMsgCount(c=>c+1);
      } catch(err) {
        setMessages(p=>[...p, {id:Date.now()+1, who:"ai", kind:"error", text:`⚠️ ${err.message}`}]);
      }
      setLoading(false);
      scroll();
      inputRef.current?.focus();
      return;
    }

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type":                              "application/json",
          "x-api-key":                                 apiKey,
          "anthropic-version":                         "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model:      "claude-sonnet-4-6",
          max_tokens: 1000,
          system:     SYSTEM(userRole, depth),
          messages:   newHist,
        }),
      });
 
      // Handle HTTP errors before trying to parse JSON
      if (!res.ok) {
        let errMsg = `API error ${res.status}`;
        try {
          const errData = await res.json();
          if (errData.error?.message) errMsg = errData.error.message;
        } catch(_) {}
        if (res.status === 429) errMsg = "Rate limit reached. Wait a moment before sending another message.";
        if (res.status === 401) errMsg = "Invalid API key. Switch role and re-enter your key.";
        throw new Error(errMsg);
      }
 
      const data = await res.json();
      const txt  = (data.content||[]).filter(b=>b.type==="text").map(b=>b.text).join("\n");
 
      if (!txt) throw new Error("No response from model. Please try again.");
 
      const aiMsg = {id:Date.now()+1, who:"ai", text:txt};
      setMessages(p=>[...p, aiMsg]);
 
      // Keep history bounded (last 40 messages = 20 turns)
      const updatedHist = [...newHist, {role:"assistant", content:txt}];
      setHistory(updatedHist.length > 40 ? updatedHist.slice(-40) : updatedHist);
      setMsgCount(c=>c+1);
 
    } catch(err) {
      setMessages(p=>[...p, {id:Date.now()+1, who:"ai", kind:"error", text:`⚠️ ${err.message}`}]);
    }
 
    setLoading(false);
    scroll();
    inputRef.current?.focus();
  }, [input, loading, history, userRole, depth, apiKey]);

  /* ── FEEDBACK (R3, backend mode) → POST /feedback ──
     `payload` is {reaction, issue_category?, expected_correct_answer?,
     correct_source_document_id?, correct_source_url?, user_comment?} — the sheet fields are
     OPTIONAL and only sent when present (Learning Hub Increment 1, H_synthesis §2 item 11).
     A bare {reaction} (Skip, or the "correct" reaction) stays byte-identical to the pre-sheet
     payload, so this is backward-compatible with a backend that hasn't added the columns yet. */
  const sendFeedback = useCallback(async (interactionId, payload, msgId) => {
    try {
      const token = await getBackendAuthToken();
      await fetch(`${BACKEND_URL}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? {Authorization: `Bearer ${token}`} : {}),
        },
        body: JSON.stringify({
          interaction_id: interactionId,
          user_reaction: payload.reaction,
          submitter_role: ROLE_TO_BACKEND[userRole] || "FieldOperator",
          submitter_name: null,  // anonymous by default (Framework feedback spec)
          ...(payload.issue_category ? {issue_category: payload.issue_category} : {}),
          ...(payload.expected_correct_answer ? {expected_correct_answer: payload.expected_correct_answer} : {}),
          ...(payload.correct_source_document_id ? {correct_source_document_id: payload.correct_source_document_id} : {}),
          ...(payload.correct_source_url ? {correct_source_url: payload.correct_source_url} : {}),
          ...(payload.user_comment ? {user_comment: payload.user_comment} : {}),
        }),
      });
      // Keep the submitted payload on the message so the CSV export can carry it
      // (PROP-005). Until now only a boolean survived, so the structured sheet a
      // tester filled in was invisible to every batch review — the export said
      // "submitted" and nothing else.
      setMessages(p=>p.map(m=>m.id===msgId ? {...m, feedbackSent:true, feedback:payload} : m));
    } catch { /* best-effort; never disrupt the conversation */ }
  }, [userRole]);

  const clear = () => {
    setMessages([]); setHistory([]); setMsgCount(0);
    anchorIdRef.current = null;  // a new conversation has no anchor (PROP-017 §4.1)
    revokeAllPageImages();       // Rich-Media Phase 1a: free every open page image
    setConvId("C" + Date.now().toString(36).toUpperCase());  // fresh conversation id
  };

  /* ── CSV EXPORT (Phase 2 WS-7) — governing-framework conversation export schema ──
     Columns: ConversationID, Timestamp, Role, MessageType, MessageText, DocumentsCited,
     SourceRevisions, Confidence, EscalationFlag, Feedback, InteractionID, IssueCategory,
     ExpectedCorrectAnswer, CorrectSourceDocumentID, CorrectSourceURL, UserComment. RFC-4180 quoting;
     UTF-8 BOM so Excel renders accents/degree signs. No new deps — built from the in-memory
     transcript. New columns are APPENDED, never inserted, so already-ingested exports and
     the Feedback Hub parser keep working (PROP-005). */
  const exportCsv = () => {
    if (!messages.length) { alert("No conversation to export yet."); return; }
    const cell = v => `"${(v == null ? "" : String(v)).replace(/"/g, '""')}"`;
    // Columns 11-16 appended LAST so the six already-ingested 10-column exports and the
    // Feedback Hub parser keep working (PROP-005).
    //   InteractionID  — the join key into interaction_log. Without it nothing a tester
    //                    exports can be matched back to the governed record (POST /feedback
    //                    404s without a valid anchor), which is why the loop could not close.
    //   IssueCategory…UserComment — the structured "what went wrong?" sheet. It was already
    //                    being POSTed to the API but never appeared in the export, so batch
    //                    review saw only the word "submitted".
    const cols = ["ConversationID","Timestamp","Role","MessageType","MessageText",
      "DocumentsCited","SourceRevisions","Confidence","EscalationFlag","Feedback",
      "InteractionID","IssueCategory","ExpectedCorrectAnswer","CorrectSourceDocumentID",
      "CorrectSourceURL","UserComment"];
    const rows = [cols];
    const BLANK_SHEET = ["", "", "", "", ""];
    const sheetOf = m => {
      const f = m.feedback;
      if (!f) return BLANK_SHEET;
      return [f.issue_category || "", f.expected_correct_answer || "",
        f.correct_source_document_id || "", f.correct_source_url || "", f.user_comment || ""];
    };
    messages.forEach(m => {
      const ts = new Date(m.id).toISOString();
      if (m.who === "user") {
        rows.push([convId, ts, userRole, "query", m.text, "", "", "", "", "", "", ...BLANK_SHEET]);
        return;
      }
      if (m.kind === "clarify" && m.resp) {
        // GEN-006 (batch FBH-BATCH-002): confirm-before-search readbacks were exported
        // as empty "answer" rows, indistinguishable from a service failure.
        const rb = (m.resp.interpretation || {}).readback || [];
        rows.push([convId, ts, "Assistant", "clarification",
          Array.isArray(rb) ? rb.join("; ") : String(rb), "", "", "", "N", "",
          m.resp.interaction_id || "", ...BLANK_SHEET]);
        return;
      }
      if (m.kind === "backend" && m.resp) {
        const r = m.resp, g = r.guardrail || {};
        const cited = r.cited_sources || [];
        const docs = cited.map(c=>c.document_id).filter(Boolean).join("; ");
        const revs = cited.map(c=>c.revision).filter(Boolean).join("; ");
        const conf = g.confidence_score != null
          ? `${g.confidence_score}${g.confidence_band ? ` (${g.confidence_band})` : ""}`
          : (g.confidence_band || "");
        // Small-talk rows export as "conversational" so feedback on them can be excluded
        // from answer-quality analysis (the Feedback Hub copies MessageType as is).
        const mtype = r.answer_mode === "conversational" ? "conversational"
          : r.answer ? "answer"
          : (g.action === "generation_error" ? "generation_error"
          : (g.refused ? "refusal" : (g.escalated ? "escalation" : "no_answer")));
        rows.push([convId, ts, "Assistant", mtype, r.answer || "(no answer returned)",
          docs, revs, conf, g.escalated ? "Y" : "N", m.feedbackSent ? "submitted" : "",
          r.interaction_id || "", ...sheetOf(m)]);
        return;
      }
      const isErr = m.kind === "error" || (typeof m.text === "string" && m.text.startsWith("⚠️"));
      rows.push([convId, ts, "Assistant", isErr ? "error" : "answer", m.text || "",
        "", "", "", "", m.feedbackSent ? "submitted" : "", "", ...sheetOf(m)]);
    });
    const csv = rows.map(r => r.map(cell).join(",")).join("\r\n");
    const today = new Date().toISOString().slice(0, 10);
    const blob = new Blob(["﻿" + csv], {type:"text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Everest_Export_${convId}_${today}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(()=>URL.revokeObjectURL(url), 0);
  };

  /* ── SSO GATE ── */
  // In governed mode, gate the app behind BJ Entra sign-in. The effect above redirects;
  // this splash shows during the hand-off (and as a manual fallback).
  if (USE_BACKEND && !account) {
    return <SignInSplash authError={getAuthError()}/>;
  }

  /* ── LOGIN GATE (legacy direct-to-Anthropic mode only) ──
     In governed mode, identity comes from BJ SSO and the app enters directly (see the account
     effect above) — there is no manual sign-in / name entry / role dropdown step. */
  if (!USE_BACKEND && !launched) {
    return (
      <Setup defaultName={account?.name || ""} onLaunch={(n, r, k) => {
        setUserName(n);
        setUserRole(r);
        setApiKey(k);
        setLaunched(true);
      }}/>
    );
  }
 
  /* ── SIDEBAR NAV ── */
  const NAV = [
    {icon:"💬", label:"Knowledge Hub",        active:true},
    {icon:"🔧", label:"Field Troubleshooting", badge:"P2"},
    {icon:"📊", label:"RCA Workbench",          badge:"P2"},
    {icon:"📄", label:"Document Drafting",      badge:"P3"},
  ];
  const ACT = [
    {icon:"📁", label:"Knowledge Library", fn:()=>window.open("https://bjenergysolutions.sharepoint.com/sites/KnowledgeLibrary","_blank")},
    {icon:"📥", label:"Export Conversation", fn:exportCsv},
    {icon:"🗑️", label:"Clear Session",      fn:clear},
    // Governed mode: identity + role come from BJ SSO, so the action is a full Entra sign-out.
    // Legacy direct mode keeps "Switch Role" to return to the local setup screen.
    ...(USE_BACKEND
      ? [{icon:"🚪", label:"Sign Out", fn:()=>signOut()}]
      : [{icon:"⚙️", label:"Switch Role", fn:()=>{setLaunched(false);setApiKey("");clear();}}]),
  ];
 
  /* ── RENDER ── */
  const conn = deriveStatus(health);
  return (
    <div style={{display:"flex",height:"100vh",overflow:"hidden",
      fontFamily:"'Barlow',system-ui,sans-serif",background:BJ.surface,color:BJ.textPri,fontSize:15}}>
 
      {/* ── SIDEBAR ── */}
      <div style={{width:260,flexShrink:0,background:BJ.blue,display:"flex",
        flexDirection:"column",color:"#fff",overflowY:"auto"}}>
 
        {/* Logo + user */}
        <div style={{padding:"20px 18px 16px",borderBottom:"1px solid rgba(255,255,255,.09)"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
            <BJLogo size={32}/>
            <div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:17,fontWeight:700,lineHeight:1.1}}>Project Everest</div>
              <div style={{fontSize:9.5,color:"rgba(255,255,255,.4)",letterSpacing:1.5,textTransform:"uppercase"}}>Knowledge Platform</div>
            </div>
          </div>
          <div style={{marginTop:13,background:"rgba(255,255,255,.08)",borderRadius:8,
            padding:"9px 11px",display:"flex",alignItems:"center",gap:9}}>
            <div style={{width:30,height:30,borderRadius:"50%",background:BJ.orange,
              display:"flex",alignItems:"center",justifyContent:"center",
              fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,flexShrink:0}}>
              {initial}
            </div>
            <div>
              <div style={{fontSize:13,fontWeight:600,lineHeight:1.2}}>{userName}</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,.42)",letterSpacing:.3}}>{userRole}</div>
            </div>
          </div>
        </div>
 
        {/* Workspaces */}
        <div style={{padding:"14px 10px 6px"}}>
          <div style={{fontSize:9.5,fontWeight:600,letterSpacing:1.5,color:"rgba(255,255,255,.35)",
            textTransform:"uppercase",padding:"0 8px",marginBottom:5}}>Workspaces</div>
          {NAV.map(n=>(
            <div key={n.label}
              onClick={()=>!n.active && alert(`${n.label} — coming in Phase ${n.badge?.replace("P","")}`)}
              style={{display:"flex",alignItems:"center",gap:9,padding:"8px 10px",borderRadius:8,
                cursor:"pointer",marginBottom:2,
                background:n.active?"rgba(255,255,255,.14)":"transparent",
                color:n.active?"#fff":"rgba(255,255,255,.65)",
                fontWeight:n.active?600:400,fontSize:13.5}}>
              <span style={{width:20,textAlign:"center"}}>{n.icon}</span>
              <span style={{flex:1}}>{n.label}</span>
              {n.badge && (
                <span style={{background:"rgba(255,255,255,.12)",color:"rgba(255,255,255,.5)",
                  fontSize:9.5,fontWeight:700,padding:"2px 6px",borderRadius:20}}>{n.badge}</span>
              )}
              {n.active && (
                <span style={{background:BJ.orange,color:"#fff",fontSize:9.5,fontWeight:700,padding:"2px 7px",borderRadius:20}}>
                  {msgCount}
                </span>
              )}
            </div>
          ))}
        </div>
 
        <hr style={{border:"none",borderTop:"1px solid rgba(255,255,255,.08)",margin:"6px 12px"}}/>
 
        {/* Actions */}
        <div style={{padding:"4px 10px 8px"}}>
          <div style={{fontSize:9.5,fontWeight:600,letterSpacing:1.5,color:"rgba(255,255,255,.35)",
            textTransform:"uppercase",padding:"0 8px",marginBottom:5}}>Actions</div>
          {ACT.map(a=>(
            <div key={a.label} onClick={a.fn}
              style={{display:"flex",alignItems:"center",gap:9,padding:"8px 10px",borderRadius:8,
                cursor:"pointer",color:"rgba(255,255,255,.65)",fontSize:13.5,marginBottom:2,
                transition:"background .12s"}}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.08)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <span style={{width:20,textAlign:"center"}}>{a.icon}</span>
              <span>{a.label}</span>
            </div>
          ))}
        </div>
 
        {/* Footer */}
        <div style={{marginTop:"auto",padding:"12px 18px",
          borderTop:"1px solid rgba(255,255,255,.08)",
          fontSize:10,color:"rgba(255,255,255,.28)",lineHeight:1.55}}>
          Project Everest v0.1 · Beta Client<br/>
          Verify all answers against source documents<br/>
          © BJ Energy Solutions
        </div>
      </div>
 
      {/* ── MAIN PANEL ── */}
      <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0,overflow:"hidden"}}>
 
        {/* Status bar — real backend / knowledge-base readiness (R2). Replaces the old
            always-green "Connected" label, which implied a live connection that didn't exist. */}
        <div style={{background:BJ.blue,color:"rgba(255,255,255,.78)",fontSize:11,
          padding:"3px 20px",display:"flex",alignItems:"center",gap:8,flexShrink:0,
          position:"relative",zIndex:25}}>
          <Dot color={conn.color} size={6}/>
          <span>{conn.label}</span>
          <span style={{opacity:.45}}>·</span>
          <span style={{opacity:.75}}>{userRole}</span>
          <button onClick={()=>setStatusOpen(o=>!o)}
            style={{background:"none",border:"none",color:"rgba(255,255,255,.7)",cursor:"pointer",
              fontSize:11,padding:0,fontFamily:"inherit",textDecoration:"underline"}}>
            details
          </button>
          {!USE_BACKEND && (
            <span style={{marginLeft:"auto",opacity:.7,fontStyle:"italic"}}>
              Chat answers are not yet served from the knowledge base.
            </span>
          )}

          {statusOpen && (
            <>
              <div style={{position:"fixed",inset:0,zIndex:30}} onClick={()=>setStatusOpen(false)}/>
              <div style={{position:"absolute",top:26,left:16,background:BJ.card,color:BJ.textPri,
                border:`1px solid ${BJ.border}`,borderRadius:10,boxShadow:"0 8px 32px rgba(0,0,0,.18)",
                transformOrigin:"top left",animation:"evMenu var(--ev-dur-base) var(--ev-ease-out) both",
                padding:"11px 13px",zIndex:40,minWidth:320,fontSize:12,lineHeight:1.5}}>
                <div style={{fontWeight:700,color:BJ.blue,marginBottom:5}}>Knowledge-base connection</div>
                {health===undefined && <div style={{color:BJ.textMut}}>Checking…</div>}
                {health===null && (
                  <div style={{color:BJ.textSec}}>
                    Backend unreachable{BACKEND_URL ? "" : " — no backend URL configured yet"}. The
                    indicator turns green once the backend is provisioned and the Knowledge Library is ingested.
                  </div>
                )}
                {health && typeof health==="object" && (
                  <>
                    <StatusRow label="Connection" state={conn.color===BJ.green?"ok":"warn"}
                      value={conn.color===BJ.green ? "Knowledge base connected" : conn.label}/>
                    <StatusRow label="Documents"  state={(health.chunks_embedded||0)>0?"ok":"warn"}
                      value={`${health.documents_total??0} loaded`}/>
                    {typeCounts && ["IT","ProjectLead"].includes(ROLE_TO_BACKEND[userRole]) && (
                      <div style={{marginTop:8,borderTop:`1px solid ${BJ.border}`,paddingTop:7}}>
                        <div style={{fontWeight:600,color:BJ.textSec,marginBottom:4}}>
                          By document type · {typeCounts.total} ingested
                        </div>
                        <div style={{maxHeight:200,overflowY:"auto",paddingRight:4}}>
                          {Object.entries(typeCounts.by_type).map(([t,n])=>(
                            <div key={t} style={{display:"flex",justifyContent:"space-between",
                              gap:16,marginTop:2}}>
                              <span style={{color:BJ.textSec}}>{t}</span>
                              <span style={{color:BJ.textPri,fontVariantNumeric:"tabular-nums"}}>{n}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
 
        {/* Top bar */}
        <div style={{height:52,background:BJ.card,borderBottom:`1px solid ${BJ.border}`,
          display:"flex",alignItems:"center",padding:"0 20px",gap:12,flexShrink:0,
          position:"relative",zIndex:20}}>
          <div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:17,fontWeight:700,color:BJ.blue}}>
              Knowledge Hub
            </div>
            <div style={{fontSize:11.5,color:BJ.textMut}}>TITAN · Truduo · Operations · Safety · Maintenance</div>
          </div>
          <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
 
            {/* Depth selector */}
            <div onClick={()=>setDepthOpen(o=>!o)}
              style={{display:"flex",alignItems:"center",gap:6,padding:"5px 12px",
                border:`1.5px solid ${BJ.borderMid}`,borderRadius:20,cursor:"pointer",
                fontSize:12.5,fontWeight:500,background:BJ.card,color:BJ.textSec,userSelect:"none"}}>
              <Dot color={depthOpt.dot}/>
              <span>{depthOpt.label}</span>
              <span style={{fontSize:10,color:BJ.textMut}}>▾</span>
            </div>
 
            {/* Clear */}
            <button onClick={clear} className="ev-btn"
              style={{width:34,height:34,border:`1.5px solid ${BJ.borderMid}`,
                borderRadius:8,background:BJ.card,cursor:"pointer",fontSize:15,color:BJ.textSec}}>
              🗑️
            </button>
          </div>
 
          {/* Depth dropdown */}
          {depthOpen && (
            <>
              <div style={{position:"fixed",inset:0,zIndex:30}} onClick={()=>setDepthOpen(false)}/>
              <div style={{position:"absolute",top:56,right:20,background:BJ.card,
                border:`1px solid ${BJ.border}`,borderRadius:14,transformOrigin:"top right",
                animation:"evMenu var(--ev-dur-base) var(--ev-ease-out) both",
                boxShadow:"0 8px 32px rgba(0,0,0,.13)",padding:6,zIndex:40,minWidth:290}}>
                {DEPTHS.map(d=>(
                  <div key={d.key} onClick={()=>{setDepth(d.key);setDepthOpen(false);}}
                    style={{padding:"9px 12px",borderRadius:8,cursor:"pointer",
                      background:depth===d.key?BJ.blueLight:"transparent"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <Dot color={d.dot}/>
                      <span style={{fontSize:13,fontWeight:600,color:BJ.textPri}}>{d.label}</span>
                    </div>
                    <div style={{fontSize:11,color:BJ.textMut,marginTop:2,paddingLeft:16,lineHeight:1.4}}>{d.desc}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
 
        {/* ── BETA CLIENT DISCLAIMER BANNER ── */}
        <div style={{background:BJ.blueLight,borderBottom:`1px solid ${BJ.blueBorder}`,
          padding:"7px 20px",lineHeight:1.4,flexShrink:0}}>
          <div style={{fontSize:12.5,fontWeight:700,color:BJ.blue,letterSpacing:.2}}>Beta Client</div>
          <div style={{fontSize:11,color:BJ.textSec,marginTop:1}}>
            Everest may make mistakes. Please verify answers with the supplied information links, documents or with SME/Engineering.
          </div>
        </div>

        {/* ── CHAT ── */}
        <div ref={chatRef} style={{flex:1,overflowY:"auto",padding:"22px 20px"}}>
 
          {/* Welcome screen */}
          {messages.length===0 && (
            <div style={{maxWidth:640,margin:"0 auto 22px"}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:25,fontWeight:700,
                color:BJ.blue,marginBottom:6}}>
                {greeting}, {userName.split(" ")[0]}
              </div>
              <p style={{fontSize:14,color:BJ.textSec,lineHeight:1.65,marginBottom:18}}>
                You're in the <b>Project Everest Knowledge Hub</b>. Ask about BJ Energy
                equipment, operating procedures, maintenance, or safety standards. Everest may make
                mistakes — verify answers with the supplied links, documents, or with your SME/Engineering.
              </p>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {roleData.prompts.map((p,i)=>(
                  <button key={i} className="ev-btn" onClick={()=>send(p)}
                    style={{background:BJ.card,border:`1.5px solid ${BJ.borderMid}`,borderRadius:10,
                      padding:"10px 14px",fontFamily:"inherit",fontSize:13,color:BJ.textSec,
                      textAlign:"left",cursor:"pointer",lineHeight:1.4,transition:"all .15s",
                      animation:"evRise var(--ev-dur-enter) var(--ev-ease-out) both",animationDelay:`${i*40}ms`}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=BJ.blueMid;e.currentTarget.style.color=BJ.blue;e.currentTarget.style.background=BJ.blueLight;}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=BJ.borderMid;e.currentTarget.style.color=BJ.textSec;e.currentTarget.style.background=BJ.card;}}>
                    💬 {p}
                  </button>
                ))}
              </div>
            </div>
          )}
 
          {/* Message list */}
          {messages.map(msg=>{
            const isUser    = msg.who === "user";
            const isBackend = msg.kind === "backend";
            const isClarify = msg.kind === "clarify"; // confirm-before-search readback (PL 2026-07-07)
            const isError   = msg.kind === "error";  // system/auth/network error — not equipment guidance, so no tags/safety callout
            const isSafe    = !isUser && !isBackend && !isClarify && !isError && /⚠|safety notice|H2S|pressure|turbine|electrical|interlock|treating iron/i.test(msg.text||"");
            const msgTags   = (isUser || isBackend || isClarify || isError) ? [] : tags(msg.text||"");
 
            return (
              <div key={msg.id} style={{maxWidth:760,margin:"0 auto 16px",display:"flex",
                flexDirection:"column",alignItems:isUser?"flex-end":"flex-start",
                animation:"evRise var(--ev-dur-enter) var(--ev-ease-out) both"}}>
                {/* maxWidth:"100%" here + minWidth:0 on the bubble: together they stop a wide
                    table from widening the bubble past a phone screen, so the table scrolls
                    inside its own wrapper instead (DEV-LEAD F-1, 2026-10-04; minWidth:0
                    alone has no effect). No change at desktop widths. */}
                <div style={{display:"flex",gap:10,alignItems:"flex-start",maxWidth:"100%",
                  flexDirection:isUser?"row-reverse":"row"}}>
                  {/* Avatar */}
                  <div style={{width:28,height:28,borderRadius:"50%",flexShrink:0,
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:12,fontWeight:700,marginTop:2,
                    background:isUser?BJ.orangeLight:BJ.blueLight,
                    color:isUser?BJ.orange:BJ.blue}}>
                    {isUser ? initial : <img src={thinkingIcon} alt="Everest" style={{width:18,height:18,display:"block"}}/>}
                  </div>
                  {/* Bubble */}
                  <div style={{maxWidth:"82%",minWidth:0}}>
                    <div style={{padding:"11px 15px",
                      borderRadius:isUser?"18px 4px 18px 18px":"4px 18px 18px 18px",
                      background:isUser?BJ.blue:BJ.card,
                      color:isUser?"#fff":BJ.textPri,
                      border:isUser?"none":`1px solid ${BJ.border}`,
                      boxShadow:"0 1px 3px rgba(0,0,0,0.07)",
                      fontSize:14,lineHeight:1.7}}>
                      {isUser
                        ? <span>{msg.text}</span>
                        : (isClarify && tcrClarifyReason(msg.resp))
                          ? (
                            /* PROP-017 §5 / §4.2 row 1: a TCR clarification. Nothing was
                               searched and there is no interpretation to confirm, so there is
                               no Confirm button (server side, U-12); Edit only. The readback
                               is the server's; the fallback is the spec's copy verbatim. */
                            <div data-turn-context="clarify" data-clarify-reason={tcrClarifyReason(msg.resp)}>
                              {(((msg.resp.interpretation || {}).readback) || []).length > 0
                                ? (msg.resp.interpretation.readback.map((r,i)=>(
                                    <div key={`trb${i}`} style={{marginBottom:6}}>{r}</div>
                                  )))
                                : (
                                  <div style={{marginBottom:6}}>
                                    {tcrClarifyReason(msg.resp) === "drift_unresolved"
                                      ? <>I couldn't tell what '{msg.originalText}' refers to. Which equipment or document do you mean?</>
                                      : <>I couldn't tell what '{msg.originalText}' means. Please ask your question in full.</>}
                                  </div>
                                )}
                              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:8}}>
                                <button className="ev-btn"
                                  onClick={()=>{setInput(msg.originalText||""); inputRef.current?.focus();}}
                                  style={{background:"transparent",color:BJ.blue,
                                    border:`1px solid ${BJ.blue}`,borderRadius:8,
                                    padding:"7px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                                  Edit my question
                                </button>
                              </div>
                            </div>
                          )
                        : isClarify
                          ? (
                            <div>
                              <div style={{fontWeight:700,marginBottom:6}}>
                                Before I search, please confirm I read this correctly:
                              </div>
                              <ul style={{margin:"0 0 8px 18px",padding:0}}>
                                {(msg.resp.interpretation.readback||[]).map((r,i)=>(
                                  <li key={`rb${i}`} style={{marginBottom:3}}>{r}</li>
                                ))}
                              </ul>
                              {(msg.resp.interpretation.ambiguities||[]).length>0 && (
                                <div style={{background:"#FFF3CD",border:"1px solid #FFD180",
                                  borderRadius:8,padding:"7px 10px",fontSize:13,marginBottom:8}}>
                                  {(msg.resp.interpretation.ambiguities||[]).map((a,i)=>(
                                    <div key={`am${i}`}>{a}</div>
                                  ))}
                                </div>
                              )}
                              <div style={{fontSize:12,color:BJ.textSec,marginBottom:8}}>
                                I&rsquo;ll search for: &ldquo;{msg.resp.interpretation.normalized_query}&rdquo;
                              </div>
                              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                                <button className="ev-btn"
                                  onClick={()=>send(msg.resp.interpretation.normalized_query,{confirmed:true})}
                                  style={{background:BJ.blue,color:"#fff",border:"none",borderRadius:8,
                                    padding:"7px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                                  ✓ Looks right — search
                                </button>
                                <button className="ev-btn"
                                  onClick={()=>{setInput(msg.originalText||""); inputRef.current?.focus();}}
                                  style={{background:"transparent",color:BJ.blue,
                                    border:`1px solid ${BJ.blue}`,borderRadius:8,
                                    padding:"7px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                                  ✎ Edit my question
                                </button>
                              </div>
                            </div>
                          )
                        : isBackend
                          ? <BackendAnswer resp={msg.resp} feedbackSent={msg.feedbackSent}
                              backendRole={ROLE_TO_BACKEND[userRole] || "FieldOperator"}
                              onFeedback={payload=>sendFeedback(msg.resp.interaction_id, payload, msg.id)}/>
                          : <span className="ev-rich" dangerouslySetInnerHTML={{__html:fmt(msg.text)}}/>
                      }
                      {/* Tags */}
                      {!isUser && msgTags.length>0 && (
                        <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:8}}>
                          {msgTags.map(t=>(
                            <span key={t.k} style={{fontSize:11,fontWeight:600,padding:"3px 9px",
                              borderRadius:20,background:t.bg,color:t.c,letterSpacing:.3}}>
                              {t.l}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Safety callout */}
                    {isSafe && (
                      <div style={{background:"#FFF3CD",border:"1px solid #FFD180",
                        borderLeft:`4px solid ${BJ.amber}`,borderRadius:"0 8px 8px 0",
                        padding:"8px 12px",marginTop:7,fontSize:12.5,color:"#6D4C00",lineHeight:1.5}}>
                        <b style={{color:"#4D2C00"}}>⚠️ Safety-Critical</b> — Verify with your SME or Field Leader before acting.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
 
          {/* Loading indicator */}
          {loading && (
            <div role="status" aria-live="polite" style={{maxWidth:760,margin:"0 auto 16px",display:"flex",
              gap:10,alignItems:"flex-start",animation:"evFade var(--ev-dur-base) var(--ev-ease-out) both"}}>
              <div style={{width:28,height:28,borderRadius:"50%",background:BJ.blueLight,
                color:BJ.blue,display:"flex",alignItems:"center",
                justifyContent:"center",fontSize:12,fontWeight:700,flexShrink:0}}><img src={thinkingIcon} alt="Everest" style={{width:18,height:18,display:"block"}}/></div>
              <div style={{padding:"12px 16px",background:BJ.card,border:`1px solid ${BJ.border}`,
                borderRadius:"4px 18px 18px 18px",display:"flex",alignItems:"center",gap:6}}>
                {[0,1,2].map(n=>(
                  <div key={n} style={{width:6,height:6,borderRadius:"50%",background:BJ.blue,
                    animation:`bn .9s ${n*.2}s infinite`}}/>
                ))}
                <span style={{fontSize:12,color:BJ.textMut,marginLeft:4}}>Thinking…</span>
              </div>
            </div>
          )}
        </div>
 
        {/* ── INPUT ── */}
        <div style={{padding:"12px 20px",borderTop:`1px solid ${BJ.border}`,
          background:BJ.card,flexShrink:0}}>
          <div style={{maxWidth:760,margin:"0 auto",display:"flex",gap:10,alignItems:"flex-end"}}>
            <textarea ref={inputRef} value={input} rows={1}
              onChange={e=>setInput(e.target.value)}
              placeholder="Ask about TITAN operations, maintenance, safety, or procedures…"
              onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
              onInput={e=>{e.target.style.height="auto";e.target.style.height=Math.min(e.target.scrollHeight,120)+"px";}}
              style={{flex:1,background:BJ.surface,border:`1.5px solid ${BJ.borderMid}`,
                borderRadius:14,padding:"11px 15px",fontFamily:"inherit",fontSize:14,
                color:BJ.textPri,outline:"none",resize:"none",maxHeight:120,lineHeight:1.5}}/>
            <button onClick={()=>send()} disabled={loading||!input.trim()} className="ev-btn"
              style={{width:42,height:42,borderRadius:11,
                background:loading||!input.trim()?BJ.surface2:BJ.blue,
                border:"none",cursor:loading||!input.trim()?"not-allowed":"pointer",
                display:"flex",alignItems:"center",justifyContent:"center",
                flexShrink:0,fontSize:17,color:loading||!input.trim()?BJ.textMut:"#fff",
                transition:"background var(--ev-dur-fast) var(--ev-ease-soft),transform var(--ev-dur-fast) var(--ev-ease-soft)"}}>
              ➤
            </button>
          </div>
          <div style={{fontSize:10.5,color:BJ.textMut,textAlign:"center",marginTop:6}}>
            Everest may make mistakes. Verify answers with the supplied links, documents, or with SME/Engineering.
          </div>
        </div>
 
      </div>
    </div>
  );
}